import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { X, History, Calendar, User, Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { getHoursChangeLog } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';

interface ChangeLogEntry {
  _id: string;
  changeType: string;
  area: string;
  previousValue: number;
  newValue: number;
  targetMonth?: number;
  targetYear?: number;
  reason?: string;
  performedBy: {
    name: string;
    email: string;
  };
  createdAt: string;
}

interface HoursChangelogModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const HoursChangelogModal: React.FC<HoursChangelogModalProps> = ({
  projectId,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [changelog, setChangelog] = useState<ChangeLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadChangelog();
    }
  }, [isOpen, projectId]);

  const loadChangelog = async (offset = 0) => {
    try {
      setLoading(true);
      const response = await getHoursChangeLog(projectId, {
        limit: 20,
        offset
      });
      
      const data = response.data.data;
      if (offset === 0) {
        setChangelog(data.changelog);
      } else {
        setChangelog(prev => [...prev, ...data.changelog]);
      }
      
      setHasMore(data.changelog.length === 20);
      setPage(offset / 20);
    } catch (error) {
      console.error('Error loading changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'default_created': 'Padrão criado',
      'default_updated': 'Padrão atualizado',
      'override_created': 'Override criado',
      'override_reset': 'Reset para padrão',
      'override_updated': 'Override atualizado'
    };
    return labels[type] || type;
  };

  const getChangeTypeColor = (type: string) => {
    if (type.includes('default')) return 'text-blue-400';
    if (type.includes('override')) return 'text-yellow-400';
    if (type.includes('reset')) return 'text-green-400';
    return 'text-foreground-secondary';
  };

  const formatDate = (dateString: string) => formatDateTime(dateString);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-4xl max-h-[80vh] bg-background-secondary border border-white/10 overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-white/10">
          <CardTitle className="text-xl font-heading text-foreground-primary flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Mudanças - Banco de Horas
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
          {loading && changelog.length === 0 ? (
            <div className="text-center text-foreground-tertiary py-8">
              Carregando histórico...
            </div>
          ) : changelog.length === 0 ? (
            <div className="text-center text-foreground-tertiary py-8">
              Nenhuma mudança registrada ainda
            </div>
          ) : (
            <div className="space-y-4">
              {changelog.map((entry) => (
                <div
                  key={entry._id}
                  className="border border-white/10 rounded-lg p-4 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-sm font-medium ${getChangeTypeColor(entry.changeType)}`}>
                          {getChangeTypeLabel(entry.changeType)}
                        </span>
                        <span className="text-sm text-foreground-secondary">
                          {entry.area}
                        </span>
                        {entry.targetMonth && entry.targetYear && (
                          <span className="text-sm text-foreground-tertiary flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {monthNames[entry.targetMonth - 1]} {entry.targetYear}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-2">
                          {entry.newValue > entry.previousValue ? (
                            <TrendingUp className="w-4 h-4 text-green-400" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-foreground-primary">
                            {entry.previousValue}h → {entry.newValue}h
                          </span>
                          <span className="text-xs text-foreground-tertiary">
                            ({entry.newValue > entry.previousValue ? '+' : ''}{entry.newValue - entry.previousValue}h)
                          </span>
                        </div>
                      </div>

                      {entry.reason && (
                        <p className="text-sm text-foreground-secondary italic mb-2">
                          "{entry.reason}"
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {entry.performedBy?.name || 'Sistema'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(entry.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => loadChangelog((page + 1) * 20)}
                    disabled={loading}
                    className="border-white/20 hover:bg-white/10"
                  >
                    {loading ? 'Carregando...' : 'Carregar mais'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
